import { SyntheticEvent, useState } from 'react';

/**
 * ProductImage — drop-in replacement for <img> when a product photo
 * may or may not exist.
 *
 * Behaviour:
 *   - If `src` exists AND loads at >= 16px × 16px → renders the photo.
 *   - Otherwise → renders a deterministic coloured tile with the product
 *     name centred on it.
 *
 * The colour is chosen by hashing the seed (typically the product id),
 * so the same product *always* gets the same tile colour. Refreshing
 * the page or re-fetching never shuffles tiles around.
 *
 * The 1×1 detection catches the template's dimensioning placeholder
 * images (which "succeed" at HTTP load but are visually empty) — onError
 * alone isn't enough for those.
 *
 * Props:
 *   src        optional image URL
 *   name       displayed inside the tile when no image
 *   seed       stable string used for colour hashing (defaults to name)
 *   className  forwarded to the wrapper
 *   imgClassName  forwarded only to the underlying <img>
 */

interface Props {
  src?: string;
  name: string;
  seed?: string;
  className?: string;
  imgClassName?: string;
  alt?: string;
  /**
   * 'auto' (default) shows the full name on big tiles and just the
   * initials on tiles smaller than ~64px. 'name' forces the full name,
   * 'initials' forces the abbreviation.
   */
  compact?: 'auto' | 'name' | 'initials';
  /**
   * Optional colour override for the placeholder tile background.
   * If provided, takes precedence over the deterministic hash. Used to
   * tie product placeholders to their category colour (Hot Drinks =
   * orange tile, Food = green tile, etc.) for a consistent visual
   * system across the POS.
   *
   * Pass a Tailwind class string like 'bg-orange'.
   */
  bgClassName?: string;
}

// 10-colour palette — saturated brand + categorical hues. White text.
// Picked for variety + tasteful contrast on a card.
const PALETTE = [
  'bg-primary',   // blue
  'bg-success',   // green
  'bg-warning',   // amber
  'bg-info',      // cyan
  'bg-purple',
  'bg-pink',
  'bg-teal',
  'bg-orange',
  'bg-indigo',
  'bg-secondary', // orange
];

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0; // force int32
  }
  return Math.abs(h);
}

function tileBgFor(seed: string): string {
  return PALETTE[hashCode(seed) % PALETTE.length];
}

function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export default function ProductImage({
  src,
  name,
  seed,
  className = '',
  imgClassName = '',
  alt,
  compact = 'auto',
  bgClassName,
}: Props) {
  const [missing, setMissing] = useState(!src);

  const onLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    // Catch placeholders that "load fine" but aren't real photos:
    //   < 16px in either dimension          → broken/dimensioning thumbnail
    //   exactly 442×200 with size ~3160B    → DreamsPOS template placeholder
    //                                         (literally has '442 x 200'
    //                                         text drawn on the JPEG)
    if (img.naturalWidth < 16 || img.naturalHeight < 16) {
      setMissing(true);
      return;
    }
    if (img.naturalWidth === 442 && img.naturalHeight === 200) {
      setMissing(true);
      return;
    }
  };

  if (missing || !src) {
    const bg = bgClassName ?? tileBgFor(seed ?? name);
    const showInitials = compact === 'initials';
    return (
      <div className={[
        bg,
        'w-full h-full flex items-center justify-center px-2 text-center',
        className,
      ].join(' ')}>
        {showInitials ? (
          <span className="text-[#FFFFFF] font-bold text-15 tracking-tight">
            {initialsOf(name)}
          </span>
        ) : (
          // Auto mode: show full name. Tailwind line-clamp keeps long names
          // tidy without overflowing on smaller cards.
          <span className="text-[#FFFFFF] font-bold text-base leading-tight tracking-tight line-clamp-3">
            {name}
          </span>
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt ?? name}
      onLoad={onLoad}
      onError={() => setMissing(true)}
      className={['w-full h-full object-cover', imgClassName].join(' ')}
      loading="lazy"
    />
  );
}
